import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AssignmentPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const courseId = parseInt(queryParams.get("course_id"));
  const timeSlotId = parseInt(queryParams.get("time_slot_id"));
  // const completed = queryParams.get("completed") === "true";  ←← WE IGNORE THIS NOW

  const studentId = Number(localStorage.getItem("student_id"));

  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [isReview, setIsReview] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExitWarning, setShowExitWarning] = useState(false);

  // ──────────────────────────────────────────────────────────────
  // REAL EXAM LOCK: If sessionStorage exists → means student already started → BLOCK FOREVER
  // This runs FIRST — on every load/reload/inspect/new tab
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const hasStartedBefore = sessionStorage.getItem(`assignment_${assignmentId}`);

    if (hasStartedBefore) {
      sessionStorage.removeItem(`assignment_${assignmentId}`);
      toast.error("Assignment session closed.", {
        autoClose: 1500,
        position: "top-center",
      });
      navigate(`/student-panel/course-curriculum/${btoa(courseId)}?time_slot_id=${btoa(timeSlotId)}`, {
        replace: true,
      });
    }
  }, [assignmentId, courseId, timeSlotId, navigate]);
  // ──────────────────────────────────────────────────────────────

  const allQuestionsAnswered = currentQuestions.length > 0 && 
    currentQuestions.every((_, index) => 
      selectedAnswers.hasOwnProperty(index) && selectedAnswers[index] !== undefined
    );

  const noQuestions = !loading && currentQuestions.length === 0;

  // Helper functions (unchanged)
  const getCorrectAnswerLetter = (question) => {
    const correctAnswer = question.correct_answer;
    if (['A', 'B', 'C', 'D'].includes(correctAnswer?.trim()?.toUpperCase())) {
      return correctAnswer.trim().toUpperCase();
    }
    const correctText = correctAnswer?.trim().toLowerCase();
    const options = {
      'A': question.option_a?.trim().toLowerCase(),
      'B': question.option_b?.trim().toLowerCase(),
      'C': question.option_c?.trim().toLowerCase(),
      'D': question.option_d?.trim().toLowerCase()
    };
    for (const [letter, text] of Object.entries(options)) {
      if (text === correctText) return letter;
    }
    return '';
  };

  const isAnswerCorrect = (question, selectedAnswer) => {
    return selectedAnswer === getCorrectAnswerLetter(question);
  };

  // ──────────────────────────────────────────────────────────────
  // Save progress automatically when user selects any answer
  // This creates the sessionStorage → triggers block on reload
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (Object.keys(selectedAnswers).length > 0 || currentQuestions.length > 0) {
      sessionStorage.setItem(
        `assignment_${assignmentId}`,
        JSON.stringify({
          currentQuestions,
          selectedAnswers,
          score,
          answeredQuestions,
        })
      );
    }
  }, [currentQuestions, selectedAnswers, score, answeredQuestions, assignmentId]);

  // Fetch questions (only runs on first fresh load)
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await fetch(SummaryApi.questionary_form_by_assignment.url, {
          method: 'POST',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assignment_id: parseInt(assignmentId) }),
        });
        const data = await response.json();
        if (data.status) {
          const questionsWithMarks = data.data.map(q => ({ ...q, marks: q.marks || 10 }));
          setCurrentQuestions(questionsWithMarks);
        } else {
          toast.error(data.message || "Failed to load questions");
        }
      } catch (err) {
        toast.error("Error loading assignment");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [assignmentId]);

  // Handle answer selection
  const handleSelectAnswer = (index, answer) => {
    setSelectedAnswers(prev => ({ ...prev, [index]: answer }));
  };

  // Submit assignment
  const handleFinish = async () => {
    // Clear session immediately on submission
    sessionStorage.removeItem(`assignment_${assignmentId}`);

    let calculatedScore = 0;
    currentQuestions.forEach((q, index) => {
      if (selectedAnswers[index] && isAnswerCorrect(q, selectedAnswers[index])) {
        calculatedScore += q.marks;
      }
    });

    setScore(calculatedScore);
    setAnsweredQuestions(selectedAnswers);

    const totalMarks = currentQuestions.reduce((t, q) => t + q.marks, 0);

    const payload = {
      assignment_id: parseInt(assignmentId),
      course_id: courseId,
      student_id: studentId,
      total_marks: totalMarks,
     pass_marks: calculatedScore,  // Use calculated pass marks (40% of total)
      is_active: 1
    };

    try {
      const response = await fetch(SummaryApi.updateassignmentresults.url, {
        method: SummaryApi.updateassignmentresults.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.status) {
        toast.success("Assignment submitted successfully!");
        
        // Only show modal AFTER successful backend submission
        setShowConfirmDialog(true);
        
        // Optional: Update other endpoints
        await Promise.all([
          fetch(SummaryApi.basedontimeslotlessonclosed.url, {
            method: SummaryApi.basedontimeslotlessonclosed.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ course_id: Number(courseId), time_slot_id: Number(timeSlotId) }),
          }),
          fetch(SummaryApi.gettopicclosed.url, {
            method: SummaryApi.gettopicclosed.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ course_id: Number(courseId), time_slot_id: Number(timeSlotId) }),
          }),
        ]).catch(() => {});
      } else {
        toast.error(data.message || "Submission failed");
      }
    } catch (err) {
      toast.error("Network error");
      console.error("Submission error:", err);
    }
  };

  const handleConfirmExit = () => {
    setShowExitWarning(false);
    sessionStorage.removeItem(`assignment_${assignmentId}`);
    navigate(`/student-panel/course-curriculum/${btoa(courseId)}?time_slot_id=${btoa(timeSlotId)}`);
  };

  const handleCancelExit = () => setShowExitWarning(false);

  if (loading) return <p className="p-8 text-center text-gray-600">Loading assignment...</p>;
  return (
    <div className="min-h-screen bg-gray-100 py-8" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial" }}>
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-blue-600 to-blue-800 text-white p-6 text-center">
            <h1 className="text-3xl font-bold">
              {isReview ? "Assignment Review" : "Assignment"}
            </h1>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {noQuestions ? (
              <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                {/* No Questions SVG */}
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 800 400" 
                  className="w-full max-w-md h-auto mb-8"
                  role="img"
                  aria-labelledby="noQuestionsTitle"
                >
                  <title id="noQuestionsTitle">No Questions Available</title>
                  
                  <defs>
                    <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#f0f9ff" />
                      <stop offset="100%" stopColor="#e0f2fe" />
                    </linearGradient>
                    <linearGradient id="docGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#93c5fd" />
                      <stop offset="100%" stopColor="#60a5fa" />
                    </linearGradient>
                    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#3b82f6" floodOpacity="0.15" />
                    </filter>
                  </defs>
                  
                  <rect width="100%" height="100%" fill="url(#bgGradient)" rx="20" />
                  
                  {/* Main Document */}
                  <g transform="translate(200, 80)">
                    <rect x="0" y="0" width="400" height="280" rx="12" fill="url(#docGradient)" filter="url(#shadow)" />
                    
                    {/* Document Lines */}
                    <g fill="white" fillOpacity="0.3">
                      <rect x="40" y="40" width="320" height="12" rx="6" />
                      <rect x="40" y="70" width="280" height="12" rx="6" />
                      <rect x="40" y="100" width="240" height="12" rx="6" />
                      <rect x="40" y="130" width="200" height="12" rx="6" />
                      <rect x="40" y="160" width="320" height="12" rx="6" />
                      <rect x="40" y="190" width="280" height="12" rx="6" />
                      <rect x="40" y="220" width="240" height="12" rx="6" />
                    </g>
                    
                    {/* Question Mark Icon */}
                    <g transform="translate(170, 100)">
                      <circle cx="30" cy="30" r="30" fill="white" fillOpacity="0.9" />
                      <text x="30" y="42" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="36" fontWeight="bold" fill="#3b82f6">?</text>
                    </g>
                    
                    {/* Cross Mark */}
                    <g transform="translate(330, 60)" stroke="#ef4444" strokeWidth="8" strokeLinecap="round">
                      <line x1="-20" y1="-20" x2="20" y2="20" />
                      <line x1="20" y1="-20" x2="-20" y2="20" />
                    </g>
                  </g>
                  
                  {/* Floating Elements */}
                  <g fill="#60a5fa" fillOpacity="0.2">
                    <circle cx="100" cy="100" r="20" />
                    <circle cx="700" cy="150" r="15" />
                    <circle cx="150" cy="300" r="25" />
                    <circle cx="650" cy="250" r="18" />
                  </g>
                  
                  {/* Text */}
                  <g transform="translate(400, 320)" textAnchor="middle">
                    <text x="0" y="0" fontFamily="Segoe UI, Roboto, sans-serif" fontSize="32" fontWeight="bold" fill="#1e40af">
                      No Questions Available
                    </text>
                    <text x="0" y="40" fontFamily="Segoe UI, Roboto, sans-serif" fontSize="18" fill="#4b5563" fillOpacity="0.8">
                      This assignment doesn't contain any questions yet.
                    </text>
                  </g>
                </svg>
                
                <div className="mt-8 text-center">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">No Questions Found</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    This assignment doesn't contain any questions at the moment. 
                    Please contact your instructor or check back later.
                  </p>
                  <button 
                    onClick={() => {
                      sessionStorage.removeItem(`assignment_${assignmentId}`);
                      navigate(`/student-panel/course-curriculum/${btoa(courseId)}?time_slot_id=${btoa(timeSlotId)}`);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition duration-200 shadow-md hover:shadow-lg"
                  >
                    Back to Course
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {isReview ? (
                  /* Review Mode - Show results */
                  <div className="space-y-8">
                    {assignmentResults && (
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl border border-green-200 text-center">
                        <p className="text-2xl font-bold text-gray-800 mb-2">
                          Your Score: <span className="text-green-600">{assignmentResults[0]?.obtained_marks || 0}</span> / {assignmentResults[0]?.total_marks || currentQuestions.reduce((t, q) => t + q.marks, 0)}
                        </p>
                        <p className="text-lg text-gray-700">
                          Pass Marks: {assignmentResults[0]?.pass_marks || Math.ceil(currentQuestions.reduce((t, q) => t + q.marks, 0) * 0.4)} / {assignmentResults[0]?.total_marks || currentQuestions.reduce((t, q) => t + q.marks, 0)}
                        </p>
                      </div>
                    )}

                    {currentQuestions.map((q, idx) => {
                      const userAnswer = answeredQuestions[idx];
                      const correctLetter = getCorrectAnswerLetter(q);
                      const isCorrect = userAnswer === correctLetter;
                      
                      return (
                        <div key={q.question_id} className="bg-gray-50 p-6 rounded-lg border">
                          <h4 className="font-bold text-lg mb-4">Question {idx + 1}: {q.question_name}</h4>
                          <div className="space-y-3">
                            {['A', 'B', 'C', 'D'].map(option => {
                              const isUserAnswer = userAnswer === option;
                              const isCorrectAnswer = correctLetter === option;
                              const optionText = q[`option_${option.toLowerCase()}`];

                              return (
                                <div
                                  key={option}
                                  className={`p-4 rounded-lg border-2 transition-all ${
                                    isCorrectAnswer
                                      ? 'bg-green-100 border-green-500'
                                      : isUserAnswer && !isCorrectAnswer
                                      ? 'bg-red-100 border-red-500'
                                      : 'bg-white border-gray-300'
                                  }`}
                                >
                                  <span className="font-semibold">{option}:</span> {optionText}
                                  {isCorrectAnswer && <span className="ml-3 text-green-700 font-bold">✓ Correct Answer</span>}
                                  {isUserAnswer && !isCorrectAnswer && <span className="ml-3 text-red-700 font-bold">✗ Your Answer</span>}
                                  {isUserAnswer && isCorrectAnswer && <span className="ml-3 text-green-700 font-bold">✓ Your Answer (Correct)</span>}
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-4 text-right">
                            <span className="text-sm font-medium text-gray-600">Marks: {q.marks}</span>
                          </div>
                        </div>
                      );
                    })}

                    <div className="text-center mt-8">
                      <button
                        onClick={() => {
                          sessionStorage.removeItem(`assignment_${assignmentId}`);
                          navigate(`/student-panel/course-curriculum/${btoa(courseId)}?time_slot_id=${btoa(timeSlotId)}`);
                        }}
                        className="bg-gray-700 hover:bg-gray-800 text-white px-8 py-3 rounded-lg font-semibold"
                      >
                        Back to Course
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Attempt Mode - Questions Form */
                  <form onSubmit={(e) => { e.preventDefault(); handleFinish(); }} className="space-y-8">
                    {currentQuestions.map((q, index) => (
                      <div key={q.question_id} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <h4 className="font-bold text-lg mb-5 text-gray-800">
                          Question {index + 1}: {q.question_name}
                          <span className="ml-4 text-sm font-normal text-gray-500">({q.marks} marks)</span>
                        </h4>
                        <div className="space-y-4">
                          {['A', 'B', 'C', 'D'].map((option) => (
                            <label
                              key={option}
                              className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                selectedAnswers[index] === option
                                  ? 'bg-blue-50 border-blue-500 shadow-md'
                                  : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`question_${index}`}
                                value={option}
                                checked={selectedAnswers[index] === option}
                                onChange={() => handleSelectAnswer(index, option)}
                                className="mr-4 h-5 w-5 text-blue-600"
                                required
                              />
                              <span className="text-gray-800 font-medium">
                                {option}: {q[`option_${option.toLowerCase()}`]}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Finish Assignment Button */}
                    <div className="mt-10 pb-10 text-center">
                      <button
                        type="submit"
                        disabled={!allQuestionsAnswered}
                        className={`px-16 py-5 rounded-xl text-2xl font-bold transition-all duration-300 shadow-xl transform hover:scale-105 ${
                          allQuestionsAnswered
                            ? 'bg-[#1f4774] hover:bg-blue-800 text-white'
                            : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        }`}
                      >
                        Finish Assignment
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clean Result Modal */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white p-6 text-center">
              <h3 className="text-3xl font-bold">Assignment Result</h3>
            </div>
            <div className="p-8 overflow-y-auto flex-1">
              {/* Score Display */}
              <div className="text-center mb-8 bg-gray-50 py-6 rounded-xl">
                <div className="text-6xl font-bold text-blue-700 mb-2">{score}</div>
                <div className="text-2xl text-gray-700">
                  out of {currentQuestions.reduce((t, q) => t + q.marks, 0)} marks
                </div>
              </div>

              {/* Answer Review Section */}
              <h4 className="text-2xl font-bold text-center mb-6 text-gray-800">Answer Review</h4>
              <div className="space-y-6">
                {currentQuestions.map((q, idx) => {
                  const userAnswer = answeredQuestions[idx];
                  const correctLetter = getCorrectAnswerLetter(q);
                  const isCorrect = userAnswer === correctLetter;
                  const userOptionText = userAnswer ? q[`option_${userAnswer.toLowerCase()}`] : 'Not Answered';
                  const correctOptionText = q[`option_${correctLetter.toLowerCase()}`];
                  
                  return (
                    <div key={q.question_id} className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                      <p className="font-semibold text-lg mb-4 text-gray-800">
                        Q{idx + 1}: {q.question_name}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-base">
                        <div>
                          <strong>Your Answer:</strong>
                          <div className={`ml-3 mt-1 p-2 rounded ${isCorrect ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                            {userAnswer ? `${userAnswer}: ${userOptionText}` : 'Not Answered'}
                            <span className="ml-2 font-bold">
                              {userAnswer ? (isCorrect ? '(Correct ✓)' : '(Incorrect ✗)') : ''}
                            </span>
                          </div>
                        </div>
                        <div>
                          <strong>Correct Answer:</strong>
                          <div className="ml-3 mt-1 p-2 rounded text-green-600 bg-green-50 font-bold">
                            {correctLetter}: {correctOptionText}
                          </div>
                        </div>
                        <div className="md:col-span-2 mt-3">
                          <strong>Marks Obtained:</strong>
                          <span className="ml-3 font-bold">
                            {isCorrect ? q.marks : 0} / {q.marks}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="p-6 bg-gray-100 text-center">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  // Clear session storage again upon navigating away from results
                  sessionStorage.removeItem(`assignment_${assignmentId}`);
                  navigate(`/student-panel/course-curriculum/${btoa(courseId)}?time_slot_id=${btoa(timeSlotId)}`);
                }}
                className="bg-[#1f4774] hover:bg-blue-800 text-white px-12 py-4 rounded-xl text-xl font-bold shadow-lg"
              >
                Back to Course
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Warning Modal */}
      {showExitWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-8 shadow-2xl text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Leave Assignment?</h3>
            <p className="text-gray-600 mb-8">
              Your progress is saved. You can come back later to finish.
            </p>
            <div className="flex gap-4">
              <button onClick={handleCancelExit} className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg font-semibold">
                Stay & Continue
              </button>
              <button onClick={handleConfirmExit} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold">
                Leave Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentPage;